import { useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import './AstroToolsSection.css'
import { FEATURE_TAGS, WHEEL_EMOJI, WHEEL_SEGMENTS } from './astroToolsData'

function FeatureTagIcon({ tag }) {
  return (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      {tag.rect && <rect width={tag.rect.width} height={tag.rect.height} x={tag.rect.x} y={tag.rect.y} rx={tag.rect.rx} />}
      {tag.dots?.map((d) => (
        <circle key={`${d.cx}-${d.cy}`} cx={d.cx} cy={d.cy} r="0.5" fill="currentColor" />
      ))}
      {tag.path && <path d={tag.path} />}
      {tag.extraPath && <path d={tag.extraPath} />}
      {tag.paths?.map((d) => (
        <path key={d} d={d} />
      ))}
    </svg>
  )
}

export default function AstroToolsSection() {
  const sectionRef = useRef(null)
  const [entered, setEntered] = useState(false)

  useEffect(() => {
    const el = sectionRef.current
    if (!el) return undefined
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (!entry.isIntersecting) return
        setEntered(true)
        observer.disconnect()
      },
      { threshold: 0.08 },
    )
    observer.observe(el)
    return () => observer.disconnect()
  }, [])

  return (
    <section id="astro-tools" ref={sectionRef}>
      <div className="absolute inset-0 pointer-events-none" aria-hidden="true">
        <svg className="absolute inset-0 w-full h-full" style={{ opacity: 0.022 }} xmlns="http://www.w3.org/2000/svg">
          <filter id="at-noise">
            <feTurbulence type="fractalNoise" baseFrequency="0.66" numOctaves="3" stitchTiles="stitch" />
            <feColorMatrix type="saturate" values="0" />
          </filter>
          <rect width="100%" height="100%" filter="url(#at-noise)" />
        </svg>
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 55% 60% at 100% 40%,rgba(232,119,15,0.14) 0%,transparent 65%)' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 45% 55% at 0% 100%,rgba(122,31,43,0.18) 0%,transparent 65%)' }} />
        <div style={{ position: 'absolute', inset: 0, background: 'radial-gradient(ellipse 40% 40% at 50% 0%,rgba(180,83,9,0.08) 0%,transparent 60%)' }} />
        <div style={{ position: 'absolute', top: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(90deg,transparent,rgba(245,158,11,0.22) 35%,rgba(245,158,11,0.30) 50%,rgba(245,158,11,0.22) 65%,transparent)' }} />
        <div style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: '1px', background: 'linear-gradient(90deg,transparent,rgba(245,158,11,0.14) 35%,rgba(245,158,11,0.20) 50%,rgba(245,158,11,0.14) 65%,transparent)' }} />
      </div>

      <div className="relative max-w-7xl mx-auto px-6 lg:px-12">
        <div id="at-grid" className="grid grid-cols-1 lg:grid-cols-[1fr_auto]" style={{ gap: '64px', alignItems: 'center' }}>
          <div className={`at-al${entered ? ' at-on' : ''}`} style={{ animationDelay: '.06s' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '18px' }}>
              <span style={{ height: '1px', width: '28px', background: 'rgba(245,158,11,0.45)', display: 'block', flexShrink: 0 }} />
              <span style={{ fontFamily: 'var(--font-heading),serif', fontSize: '9.5px', fontWeight: 600, letterSpacing: '.32em', color: '#D97706', textTransform: 'uppercase' }}>
                ॐ &nbsp;Astro Tools
              </span>
              <span style={{ height: '1px', width: '28px', background: 'rgba(245,158,11,0.45)', display: 'block', flexShrink: 0 }} />
            </div>

            <h2 className="at-heading">
              Free Vedic <span>Astro Tools</span>
            </h2>

            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
              <span style={{ height: '1px', width: '44px', background: 'linear-gradient(90deg,rgba(245,158,11,0.4),transparent)', display: 'block' }} />
              <span style={{ fontSize: '18px', color: '#F59E0B', opacity: 0.6, lineHeight: 1 }}>ॐ</span>
              <span style={{ height: '1px', width: '44px', background: 'linear-gradient(90deg,transparent,rgba(245,158,11,0.4))', display: 'block' }} />
            </div>

            <p className="at-desc">
              Generate your Kundli, check marriage compatibility, see today&apos;s Panchang and festivals, find your
              lucky picks and birth number — all free, in one place.
            </p>

            <div className="at-tags">
              {FEATURE_TAGS.map((tag) => (
                <span className="at-tag" key={tag.label}>
                  <FeatureTagIcon tag={tag} />
                  {tag.label}
                </span>
              ))}
            </div>

            <Link to="/astro-tools" className="at-cta">
              Explore Astro Tools
              <svg className="at-cta-arrow" width="16" height="16" viewBox="0 0 16 16" fill="none">
                <path d="M3 8H13M13 8L9 4M13 8L9 12" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
              </svg>
            </Link>
          </div>

          <div className={`at-wheel-wrap at-si${entered ? ' at-on' : ''}`} style={{ animationDelay: '.18s' }}>
            <div className="at-wheel-ring" style={{ position: 'relative' }}>
              <div className="at-pointer" />
              <div className="at-wheel-glow" />

              <div className="at-stat" style={{ top: '-10px', left: '-80px' }}>
                <div>
                  <div className="at-stat-num">5+</div>
                  <div className="at-stat-label">Free Tools</div>
                </div>
              </div>

              <div className="at-stat" style={{ bottom: '-10px', right: '-80px' }}>
                <div>
                  <div className="at-stat-num">100%</div>
                  <div className="at-stat-label">No Sign-Up</div>
                </div>
              </div>

              <div className="at-wheel-boost">
                <svg className="at-wheel-svg" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg">
                  <circle cx="100" cy="100" r="99" fill="#0F0903" />
                  <circle cx="100" cy="100" r="97" fill="none" stroke="#D97706" strokeWidth="1.4" opacity="0.55" />
                  <circle cx="100" cy="100" r="92" fill="none" stroke="rgba(245,158,11,0.12)" strokeWidth="0.5" />
  
                  {WHEEL_SEGMENTS.map((seg) => (
                    <path key={seg.d} d={seg.d} fill={seg.fill} stroke="#D97706" strokeWidth="0.8" opacity="0.9" />
                  ))}
  
                  {WHEEL_EMOJI.map((item) => (
                    <g key={item.angle} transform={`rotate(${item.angle} 100 100)`}>
                      <text x="100" y="34" textAnchor="middle" fontSize="13">
                        {item.icon}
                      </text>
                    </g>
                  ))}
                </svg>
              </div>

              {/* Static center medallion — outside the spinning layers so it never rotates. */}
              <svg className="at-wheel-center" viewBox="0 0 200 200" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                <circle cx="100" cy="100" r="24" fill="#0F0903" stroke="#D97706" strokeWidth="1.3" />
                <circle cx="100" cy="100" r="19" fill="#0F0903" stroke="rgba(245,158,11,0.22)" strokeWidth="0.6" />

                <text x="100" y="107" textAnchor="middle" fontSize="19" fill="#F0D9A0" style={{ filter: 'drop-shadow(0 0 7px rgba(232,119,15,0.85))' }}>
                  ॐ
                </text>
              </svg>
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}
